/**
 * NEXORA AI - Notification Center
 * Refactored: CSS custom → Tailwind + i18n + Responsive Premium
 * Epic 1.3 - Responsive Premium
 */

import React, { useState, useEffect } from 'react';
import { Bell, Check, CheckCheck, Trash2, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

const NotificationCenter = () => {
  const { t } = useTranslation('notifications');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const LIMIT = 20;

  useEffect(() => {
    fetchNotifications(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchNotifications = async (reset = false) => {
    setLoading(true);
    try {
      const currentPage = reset ? 0 : page;
      const readParam = filter === 'all' ? '' : `&read=${filter === 'read'}`;
      
      const response = await fetch(
        `${API_URL}/api/notifications?skip=${currentPage * LIMIT}&limit=${LIMIT}${readParam}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (reset) {
          setNotifications(data);
          setPage(1);
        } else {
          setNotifications([...notifications, ...data]);
          setPage(page + 1);
        }
        
        setHasMore(data.length === LIMIT);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        setNotifications(notifications.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        ));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        setNotifications(notifications.map(n => ({ ...n, read: true })));
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        setNotifications(notifications.filter(n => n.id !== notificationId));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      publication_success: { emoji: '✓', color: 'text-green-500', bg: 'bg-green-500/10' },
      publication_failed: { emoji: '✗', color: 'text-red-500', bg: 'bg-red-500/10' },
      token_expired: { emoji: '⚠', color: 'text-amber-500', bg: 'bg-amber-500/10' },
      workspace_member_added: { emoji: '👤', color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
      analytics_daily: { emoji: '📊', color: 'text-purple-500', bg: 'bg-purple-500/10' },
      analytics_weekly: { emoji: '📈', color: 'text-purple-500', bg: 'bg-purple-500/10' },
      analytics_monthly: { emoji: '📉', color: 'text-purple-500', bg: 'bg-purple-500/10' },
      ai_generation_completed: { emoji: '🤖', color: 'text-teal-500', bg: 'bg-teal-500/10' },
      video_generation_completed: { emoji: '🎬', color: 'text-pink-500', bg: 'bg-pink-500/10' },
      payment_success: { emoji: '💳', color: 'text-green-500', bg: 'bg-green-500/10' },
      payment_failed: { emoji: '💳', color: 'text-red-500', bg: 'bg-red-500/10' },
      security_alert: { emoji: '🔒', color: 'text-red-500', bg: 'bg-red-500/10' },
      nexora_update: { emoji: '✨', color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
    };
    return iconMap[type] || { emoji: '🔔', color: 'text-zinc-400', bg: 'bg-zinc-500/10' };
  };

  const getTimeAgo = (timestamp) => {
    const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
    
    if (seconds < 60) return t('time.just_now');
    if (seconds < 3600) return t('time.minutes_ago', { minutes: Math.floor(seconds / 60) });
    if (seconds < 86400) return t('time.hours_ago', { hours: Math.floor(seconds / 3600) });
    if (seconds < 604800) return t('time.days_ago', { days: Math.floor(seconds / 86400) });
    return new Date(timestamp).toLocaleDateString();
  };

  const filteredNotifications = notifications.filter(n =>
    n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    n.message.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8" data-testid="notification-center">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
                <Bell className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
                  {t('title')}
                </h1>
                {unreadCount > 0 && (
                  <p className="text-sm text-slate-600 mt-1">
                    {t('unread_count', { count: unreadCount })}
                  </p>
                )}
              </div>
            </div>
            
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-lg shadow-blue-600/30 w-full sm:w-auto"
                data-testid="mark-all-read-button"
              >
                <CheckCheck size={18} />
                <span>{t('mark_all_read')}</span>
              </button>
            )}
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder={t('search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="notification-search-input"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-2 bg-white border border-slate-200 rounded-xl p-1">
              <button
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  filter === 'all'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => setFilter('all')}
                data-testid="filter-all-button"
              >
                {t('filter.all')}
              </button>
              <button
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  filter === 'unread'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => setFilter('unread')}
                data-testid="filter-unread-button"
              >
                {t('filter.unread')}
              </button>
              <button
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  filter === 'read'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => setFilter('read')}
                data-testid="filter-read-button"
              >
                {t('filter.read')}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading && page === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-slate-600">{t('loading')}</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Bell className="w-16 h-16 text-slate-300 mb-4" strokeWidth={1} />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">{t('empty.title')}</h2>
            <p className="text-slate-600">
              {searchQuery
                ? t('empty.no_results')
                : filter === 'unread'
                ? t('empty.all_caught_up')
                : t('empty.no_notifications')}
            </p>
          </div>
        ) : (
          <>
            <motion.div
              className="grid grid-cols-1 lg:grid-cols-2 gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <AnimatePresence>
                {filteredNotifications.map((notification) => {
                  const iconData = getNotificationIcon(notification.type);
                  
                  return (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                      className={`flex gap-4 p-5 bg-white border rounded-2xl transition-all hover:shadow-md ${
                        !notification.read
                          ? 'border-blue-200 bg-blue-50/30'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`notification-card-${notification.id}`}
                    >
                      <div className={`w-12 h-12 flex items-center justify-center rounded-xl text-2xl flex-shrink-0 ${iconData.bg}`}>
                        <span className={iconData.color}>{iconData.emoji}</span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 mb-2">
                          <h3 className="flex-1 font-semibold text-slate-900 text-base leading-snug">
                            {notification.title}
                          </h3>
                          {!notification.read && (
                            <div className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 flex-shrink-0"></div>
                          )}
                        </div>
                        
                        <p className="text-sm text-slate-600 mb-3 leading-relaxed">
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">
                            {getTimeAgo(notification.created_at)}
                          </span>
                          
                          <div className="flex gap-2">
                            {!notification.read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="w-8 h-8 flex items-center justify-center bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
                                title={t('actions.mark_read')}
                                data-testid={`mark-read-${notification.id}`}
                              >
                                <Check size={16} />
                              </button>
                            )}
                            
                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="w-8 h-8 flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                              title={t('actions.delete')}
                              data-testid={`delete-${notification.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>

            {/* Load More */}
            {hasMore && !loading && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => fetchNotifications(false)}
                  className="px-8 py-3 bg-white border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 hover:border-blue-300 transition-all"
                >
                  {t('load_more')}
                </button>
              </div>
            )}

            {loading && page > 0 && (
              <div className="flex justify-center mt-8">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;
